import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { deDE, enUS } from '@mui/x-date-pickers/locales'
import dayjs, { Dayjs } from 'dayjs'
import 'dayjs/locale/de'
import 'dayjs/locale/en'
import { forwardRef, Ref, useState } from 'react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { DateValidationError, DateView } from '@mui/x-date-pickers/models'

type DatePickerProps = {
    disabled?: boolean
    label?: string
    value?: Dayjs | string | null
    onChange?: (newValue: Dayjs | null) => void
    onError?: (inputIsInvalid: boolean) => void
    required?: boolean
    minWidth?: string
    inputFieldStyles?: React.CSSProperties
    ariaLabeledBy?: string
    onBlur?: () => void
    onAccept?: (value: Dayjs | null) => void
    minDate?: Dayjs
    maxDate?: Dayjs
    hasError?: boolean
    size?: 'small' | 'medium'
    openTo?: 'day' | 'month' | 'year'
    views?: DateView[]
    format?: string
    locale?: string
}

function isValidDate(reason: DateValidationError | null, value: Dayjs | null, required = false) {
    if (required) {
        return reason === null && value !== null
    }

    return reason === null
}

const isUsEnglishLocale = (locale: string) => locale === 'en-US'

/**
 * @deprecated Use `DatePicker` from the Piazza component library for new work.
 */
const DatePicker = (props: DatePickerProps, ref: Ref<HTMLDivElement>) => {
    const {
        format,
        disabled = false,
        required = false,
        label,
        value,
        onChange,
        onError,
        minWidth,
        inputFieldStyles,
        ariaLabeledBy,
        onBlur,
        onAccept,
        maxDate,
        minDate,
        hasError,
        size = 'medium',
        openTo = 'day',
        views,
        locale = 'de-DE',
    } = props
    const [internalValue, setInternalValue] = useState<Dayjs | null>(null)

    const onChangeHandler = onChange ?? setInternalValue
    const date = typeof value === 'string' ? dayjs(value) : value !== undefined ? value : internalValue
    const usesUsEnglishLocale = isUsEnglishLocale(locale)
    const dateFormat = format ?? (usesUsEnglishLocale ? 'MM/DD/YYYY' : 'DD.MM.YYYY')
    const localeText = usesUsEnglishLocale
        ? enUS.components.MuiLocalizationProvider.defaultProps.localeText
        : deDE.components.MuiLocalizationProvider.defaultProps.localeText

    function onErrorHandler(reason: DateValidationError | null, newValue: Dayjs | null) {
        onError?.(!isValidDate(reason, newValue, required))
    }

    return (
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={usesUsEnglishLocale ? 'en' : 'de'} localeText={localeText}>
            <MuiDatePicker
                ref={ref}
                enableAccessibleFieldDOMStructure={false}
                label={label}
                value={date}
                onChange={onChangeHandler}
                openTo={openTo}
                views={views}
                disabled={disabled}
                onError={onErrorHandler}
                format={dateFormat}
                slotProps={{
                    textField: {
                        ...(hasError ? { error: true } : {}),
                        onBlur,
                        required,
                        size,
                        style: { minWidth },
                        sx: {
                            '& svg': {
                                bgcolor: disabled ? tokens.ColorSurfaceDisabled : 'inherit',
                                zIndex: 1,
                            },
                            '& .Mui-disabled.MuiOutlinedInput-root': { bgcolor: tokens.ColorSurfaceDisabled },
                            '& .Mui-disabled > .MuiOutlinedInput-notchedOutline': {
                                borderRadius: '4px',
                                borderColor: 'transparent!important',
                            },
                            ...inputFieldStyles,
                        },
                        inputProps: {
                            ...(ariaLabeledBy ? { 'aria-labelledby': ariaLabeledBy } : {}),
                        },
                    },
                }}
                onAccept={onAccept}
                minDate={minDate}
                maxDate={maxDate}
            />
        </LocalizationProvider>
    )
}

export default forwardRef(DatePicker)
